import { Component, OnDestroy, OnInit } from '@angular/core';
import { ProductsFacade } from '@kion/data-access/data-access-products';
import { ActivatedRoute } from '@angular/router';
import { SubSink } from 'subsink';
import { Observable, of } from 'rxjs';
import { Basket, MasterProductComplete, Product } from '@kion/data-models';
import { map, take } from 'rxjs/operators';
import { BasketsFacade } from '@kion/data-access/data-access-baskets';
import { CorrelationObserverService } from '@kion/ngrx-helper';
import { ToastService } from '@kion/ui';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'kion-cms-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss']
})
export class ProductDetailComponent implements OnInit, OnDestroy {

  private readonly subSink: SubSink;
  product$: Observable<Product>;
  masterProduct$: Observable<MasterProductComplete> = of(null);

  addingToBasket: boolean;

  constructor(
    private productsFacade: ProductsFacade,
    private route: ActivatedRoute,
    private basketsFacade: BasketsFacade,
    private observer: CorrelationObserverService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    this.subSink = new SubSink();
  }

  ngOnInit(): void {
    this.subSink.sink = this.route.params.subscribe(
      params => {
        if (params) {
          this.productsFacade.loadById(params.id, params.id);
          this.productsFacade.setProductActive(params.id);
          this.masterProduct$ = this.productsFacade.all$.pipe(
            map(
              (products: Product[]) => products.find(
                (product: Product) => product.id === params.id
              )
            ),
            this.productsFacade.enricher.enrichOne()
          );
        }
      }
    );
    this.subSink.sink = this.route.queryParams.subscribe(
      params => {
        if (params.variant) {
          this.selectVariantByQuery(params.variant);
          return;
        }
        this.selectFirstProductVariant();
      }
    );
  }

  ngOnDestroy(): void {
    this.productsFacade.setProductActive("");
    this.subSink.unsubscribe();
  }

  addToBasket(item: {productId: string; quantity: number, options: {code: string}[]}) {
    this.addingToBasket = true;
    this.subSink.sink = this.basketsFacade.all$.pipe(take(1)).subscribe(
      (baskets: Basket[]) =>  {
        const basket = baskets.find(b => b.id === window.localStorage.getItem('basketId'));
        this.subSink.sink = this.observer.subscribeToAction(
          this.basketsFacade.addToBasket(basket.id, item.productId, item.quantity, item.options),
          () => {
            this.toastService.show(
              {
                type: 'success',
                text: this.translateService.instant('toast.basket.add.success')
              }
            );
            this.addingToBasket = false;
          },
          () => {
            this.toastService.show(
              {
                type: 'error',
                text: this.translateService.instant('toast.basket.add.error')
              }
            );
            this.addingToBasket = false;
          }
        )
      }
    );
  }

  private selectFirstProductVariant(): void {
    this.product$ = this.masterProduct$.pipe(
      map(
        (master) => {
          if (master) {
            if (master.variants && master.variants.length > 0) {
              return master.variants[0];
            }
            return master.masterProduct;
          }
        }
      )
    )
  }

  private selectVariantByQuery(variantId: string): void {
    this.product$ = this.masterProduct$.pipe(
      map(
        (product) => product?.variants?.find(
          p => p.id === variantId
        )
      )
    )
  }
}
